import React, { useEffect, useMemo, useRef, useState } from "react";
import { listAll, updateDocById } from "../../services/firestore";
import {
  Search, X, Calendar, Filter, Download, Printer, Phone, Mail,
  CheckSquare, Square, ChevronLeft, ChevronRight, ArrowUpDown,
  Package as PackageIcon, MapPin, CreditCard, MessageSquare, RefreshCw,
  Settings, Check as CheckIcon,
} from "lucide-react";
import {
  getOrderSettings, setTrackingPrefix as saveTrackingPrefix,
  generateTrackingNo, DEFAULT_TRACKING_PREFIX,
} from "../../services/orderSettings";

// ─────────────────────────────────────────────────────────────────────
// Sifariş statusları — tam Azərbaycan dilində
// Qeyd: `awaiting_payment` və `payment_failed` admin panelində göstərilmir.
// Yalnız bank ödənişi təsdiq olunmuş sifarişlər siyahıda görünür.
// ─────────────────────────────────────────────────────────────────────
const STATUSES = [
  ["placed", "Verildi"],
  ["preparing", "Hazırlanır"],
  ["with_courier", "Kuryerdə"],
  ["on_the_way", "Yoldadır"],
  ["delivered", "Çatdırıldı"],
  ["cancelled", "Ləğv edilib"],
];
const STATUS_LABEL = Object.fromEntries(STATUSES);
// Tarixçə üçün gizli statusları da daxil edən tam siyahı (yalnız oxumaq üçün)
const FULL_STATUS_LABEL = {
  ...STATUS_LABEL,
  awaiting_payment: "Ödəniş gözləyir",
  payment_failed:   "Ödəniş uğursuz oldu",
};
const STATUS_COLOR = {
  awaiting_payment: "text-amber-500",
  payment_failed:   "text-[var(--accent)]",
  placed:           "text-blue-500",
  preparing:        "text-violet-500",
  with_courier:     "text-cyan-500",
  on_the_way:       "text-teal-500",
  delivered:        "text-emerald-500",
  cancelled:        "text-[var(--accent)]",
};

// Sifarişlər siyahısında və statistikada nəzərə alınmayan statuslar
// (ödəniş tamamlanmamış sifarişlər).
const HIDDEN_STATUSES = new Set(["awaiting_payment", "payment_failed"]);

// ─────────────────────────────────────────────────────────────────────
// Tarix dövr seçimləri
// ─────────────────────────────────────────────────────────────────────
const DATE_PRESETS = [
  ["all", "Bütün vaxtlar"],
  ["today", "Bu gün"],
  ["yesterday", "Dünən"],
  ["7d", "Son 7 gün"],
  ["30d", "Son 30 gün"],
  ["this_month", "Bu ay"],
  ["last_month", "Keçən ay"],
  ["this_year", "Bu il"],
  ["custom", "Xüsusi"],
];

const PAGE_SIZES = [25, 50, 100, 200];

const SORT_OPTIONS = [
  ["date_desc",  "Tarix (ən yeni)"],
  ["date_asc",   "Tarix (ən köhnə)"],
  ["total_desc", "Məbləğ (çox → az)"],
  ["total_asc",  "Məbləğ (az → çox)"],
  ["name_asc",   "Müştəri (A → Z)"],
  ["name_desc",  "Müştəri (Z → A)"],
];

// ─── Firestore Timestamp / Date / string → JS Date ────────────────────
function toDate(v) {
  if (!v) return null;
  if (typeof v?.toDate === "function") return v.toDate();
  if (v instanceof Date) return v;
  if (typeof v === "number") return new Date(v);
  if (typeof v === "string") {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === "object" && typeof v.seconds === "number") {
    return new Date(v.seconds * 1000);
  }
  return null;
}

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay   = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };

function rangeForPreset(preset, customFrom, customTo) {
  const now = new Date();
  switch (preset) {
    case "today":      return [startOfDay(now), endOfDay(now)];
    case "yesterday": {
      const y = new Date(now); y.setDate(y.getDate() - 1);
      return [startOfDay(y), endOfDay(y)];
    }
    case "7d":  { const a = new Date(now); a.setDate(a.getDate() - 6);  return [startOfDay(a), endOfDay(now)]; }
    case "30d": { const a = new Date(now); a.setDate(a.getDate() - 29); return [startOfDay(a), endOfDay(now)]; }
    case "this_month": { const a = new Date(now.getFullYear(), now.getMonth(), 1);    return [startOfDay(a), endOfDay(now)]; }
    case "last_month": {
      const a = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const b = new Date(now.getFullYear(), now.getMonth(), 0);
      return [startOfDay(a), endOfDay(b)];
    }
    case "this_year":  { const a = new Date(now.getFullYear(), 0, 1); return [startOfDay(a), endOfDay(now)]; }
    case "custom":
      if (!customFrom && !customTo) return [null, null];
      return [
        customFrom ? startOfDay(new Date(customFrom)) : null,
        customTo   ? endOfDay(new Date(customTo))     : null,
      ];
    default: return [null, null];
  }
}

const formatDateTime = (d) => {
  if (!d) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const formatMoney = (n) => `₼${Number(n || 0).toFixed(2)}`;
const sumQty = (items) => (items || []).reduce((s, i) => s + Number(i.qty || 0), 0);

// ─── CSV xidməti ──────────────────────────────────────────────────────
function downloadCsv(filename, rows) {
  if (!rows.length) return;
  const esc = (v) => {
    if (v == null) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n;]/.test(s) ? `"${s}"` : s;
  };
  const headers = Object.keys(rows[0]);
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
  const bom = "\uFEFF"; // Excel uchun UTF-8 BOM (azərbaycan hərfləri üçün)
  const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 200);
}

// ─── Çap pəncərəsi ────────────────────────────────────────────────────
function printOrder(order) {
  const w = window.open("", "_blank", "width=800,height=900");
  if (!w) return;
  const created = toDate(order.created_at);
  const rows = (order.items || []).map((i) => `
    <tr>
      <td>${(i.title || "").replace(/</g, "&lt;")}</td>
      <td>${(i.artist_name || "").replace(/</g, "&lt;")}</td>
      <td style="text-align:center">${i.qty}</td>
      <td style="text-align:right">${formatMoney(i.price_azn)}</td>
      <td style="text-align:right">${formatMoney(Number(i.price_azn || 0) * Number(i.qty || 0))}</td>
    </tr>`).join("");
  w.document.write(`<!doctype html><html lang="az"><head><meta charset="utf-8" />
    <title>Sifariş ${order.tracking_no}</title>
    <style>
      body { font-family: ui-sans-serif, system-ui, sans-serif; padding: 32px; color:#111; }
      h1 { font-size: 28px; margin: 0 0 4px; }
      .meta { color:#666; font-size: 12px; letter-spacing:.1em; text-transform: uppercase; }
      .info { display:grid; grid-template-columns: 1fr 1fr; gap:14px; margin: 22px 0 8px; }
      .info div { border:1px solid #ddd; padding:10px 12px; }
      .info b { display:block; font-size:11px; letter-spacing:.1em; color:#888; margin-bottom:4px; text-transform:uppercase; }
      table { width:100%; border-collapse:collapse; margin-top: 14px; }
      th, td { border-bottom:1px solid #eee; padding:8px 6px; font-size:13px; }
      th { background:#f5f5f5; text-align:left; font-size:11px; text-transform:uppercase; letter-spacing:.08em; }
      .total { text-align:right; font-size:18px; margin-top:14px; }
      .total b { font-size:22px; }
    </style></head><body>
    <div class="meta">SİFARİŞ</div>
    <h1>${order.tracking_no || "—"}</h1>
    <div class="meta">${formatDateTime(created)}</div>
    <div class="info">
      <div><b>Müştəri</b>${(order.full_name || "—").replace(/</g, "&lt;")}</div>
      <div><b>Telefon</b>${(order.phone || "—").replace(/</g, "&lt;")}</div>
      <div><b>Şəhər</b>${(order.city || "—").replace(/</g, "&lt;")}</div>
      <div><b>Çatdırılma</b>${(order.delivery_code || "—").replace(/</g, "&lt;")}</div>
      <div style="grid-column:span 2"><b>Ünvan</b>${(order.address || "—").replace(/</g, "&lt;")}</div>
      ${order.notes ? `<div style="grid-column:span 2"><b>Qeyd</b>${order.notes.replace(/</g, "&lt;")}</div>` : ""}
    </div>
    <table>
      <thead><tr><th>Məhsul</th><th>İfaçı</th><th style="text-align:center">Say</th><th style="text-align:right">Qiymət</th><th style="text-align:right">Cəmi</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="total">Cəm: <b>${formatMoney(order.total_azn)}</b></div>
    <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 500); };</script>
    </body></html>`);
  w.document.close();
}

// ─────────────────────────────────────────────────────────────────────
//  ƏSAS KOMPONENT
// ─────────────────────────────────────────────────────────────────────
export default function AdminOrders() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filterlər
  const [datePreset, setDatePreset] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("date_desc");

  // Toplu seçim
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Səhifələmə
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Daxili qeyd (modaldakı admin qeyd sahəsi)
  const [adminNote, setAdminNote] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const noteRef = useRef(null);

  // İzləmə nömrəsi prefiksinin admin tərəfindən redaktəsi
  const [trackingPrefix, setTrackingPrefixState] = useState(DEFAULT_TRACKING_PREFIX);
  const [trackingDraft, setTrackingDraft] = useState(DEFAULT_TRACKING_PREFIX);
  const [trackingEditOpen, setTrackingEditOpen] = useState(false);
  const [trackingSaving, setTrackingSaving] = useState(false);
  const [trackingSaved, setTrackingSaved] = useState(false);

  // Settings-i yüklə
  useEffect(() => {
    let cancelled = false;
    getOrderSettings().then((s) => {
      if (cancelled) return;
      const p = s?.tracking_prefix || DEFAULT_TRACKING_PREFIX;
      setTrackingPrefixState(p);
      setTrackingDraft(p);
    }).catch(() => { /* noop — defaults already in state */ });
    return () => { cancelled = true; };
  }, []);

  const saveTrackingPrefixHandler = async () => {
    setTrackingSaving(true);
    try {
      const cleaned = await saveTrackingPrefix(trackingDraft);
      setTrackingPrefixState(cleaned);
      setTrackingDraft(cleaned);
      setTrackingSaved(true);
      setTimeout(() => setTrackingSaved(false), 1800);
      setTimeout(() => setTrackingEditOpen(false), 600);
    } catch (_e) { /* swallow — keep modal open so user can retry */ }
    finally { setTrackingSaving(false); }
  };

  const reload = async () => {
    setLoading(true);
    try {
      const o = await listAll("orders", { sort: "created_at", dir: "desc" });
      // Ödənilməmiş sifarişləri (awaiting_payment, payment_failed) admin
      // siyahısından tam gizlədirik — bank ödənişi təsdiqlənməyən sifarişlər
      // burada görünməməlidir.
      setItems(o.filter((row) => !HIDDEN_STATUSES.has(row.status)));
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  // ── Status dəyişdirmək (tək sifariş)
  const setStatus = async (o, status) => {
    const history = [...(o.status_history || []), { status, ts: new Date().toISOString() }];
    await updateDocById("orders", o.id, { status, status_history: history });
    await reload();
    if (active?.id === o.id) setActive({ ...active, status, status_history: history });
  };

  // ── Toplu status dəyişdirmək
  const bulkSetStatus = async (status) => {
    if (!selectedIds.size) return;
    if (!window.confirm(`${selectedIds.size} sifariş üçün status “${STATUS_LABEL[status] || status}” olaraq yenilənsin?`)) return;
    setBulkBusy(true);
    try {
      for (const id of selectedIds) {
        const o = items.find((x) => x.id === id);
        if (!o) continue;
        const history = [...(o.status_history || []), { status, ts: new Date().toISOString() }];
        // eslint-disable-next-line no-await-in-loop
        await updateDocById("orders", o.id, { status, status_history: history });
      }
      setSelectedIds(new Set());
      await reload();
    } finally { setBulkBusy(false); }
  };

  // ── Daxili qeyd saxla (modaldan)
  const saveAdminNote = async () => {
    if (!active) return;
    setNoteSaving(true);
    try {
      await updateDocById("orders", active.id, { admin_note: adminNote });
      setActive({ ...active, admin_note: adminNote });
      await reload();
    } finally { setNoteSaving(false); }
  };
  useEffect(() => {
    setAdminNote(active?.admin_note || "");
  }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Müştəriyə açıq qeyd (sifarişlərim səhifəsində görsənir)
  const [customerNote, setCustomerNote] = useState("");
  const [customerNoteSaving, setCustomerNoteSaving] = useState(false);
  const [customerNoteSaved, setCustomerNoteSaved] = useState(false);
  const saveCustomerNote = async () => {
    if (!active) return;
    setCustomerNoteSaving(true);
    try {
      await updateDocById("orders", active.id, { customer_visible_note: customerNote });
      setActive({ ...active, customer_visible_note: customerNote });
      setCustomerNoteSaved(true);
      setTimeout(() => setCustomerNoteSaved(false), 1800);
      await reload();
    } finally { setCustomerNoteSaving(false); }
  };
  useEffect(() => {
    setCustomerNote(active?.customer_visible_note || "");
  }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Status sayğacları (filterlərdən asılı olmayan ümumi)
  const statusCounts = useMemo(() => {
    const m = { all: items.length };
    for (const o of items) m[o.status || "placed"] = (m[o.status || "placed"] || 0) + 1;
    return m;
  }, [items]);

  // ── Şəhərlər siyahısı (filtr üçün)
  const cityOptions = useMemo(() => {
    const m = new Map();
    for (const o of items) {
      const c = (o.city || "").trim();
      if (!c) continue;
      m.set(c, (m.get(c) || 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [items]);

  // ── Filtrasiya
  const filtered = useMemo(() => {
    const [from, to] = rangeForPreset(datePreset, customFrom, customTo);
    const term = query.trim().toLowerCase();
    return items.filter((o) => {
      if (statusFilter !== "all" && (o.status || "placed") !== statusFilter) return false;
      if (cityFilter !== "all" && (o.city || "") !== cityFilter) return false;
      if (from || to) {
        const dt = toDate(o.created_at);
        if (!dt) return false;
        if (from && dt < from) return false;
        if (to && dt > to)   return false;
      }
      if (term) {
        const hay = [o.tracking_no, o.full_name, o.phone, o.email, o.city, o.address]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [items, datePreset, customFrom, customTo, statusFilter, cityFilter, query]);

  // ── Sıralama
  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dateOf = (o) => toDate(o.created_at)?.getTime() || 0;
    const cmp = {
      date_desc:  (a, b) => dateOf(b) - dateOf(a),
      date_asc:   (a, b) => dateOf(a) - dateOf(b),
      total_desc: (a, b) => Number(b.total_azn || 0) - Number(a.total_azn || 0),
      total_asc:  (a, b) => Number(a.total_azn || 0) - Number(b.total_azn || 0),
      name_asc:   (a, b) => (a.full_name || "").localeCompare(b.full_name || "", "az"),
      name_desc:  (a, b) => (b.full_name || "").localeCompare(a.full_name || "", "az"),
    }[sortKey] || (() => 0);
    return arr.sort(cmp);
  }, [filtered, sortKey]);

  // ── Səhifələnmiş sətirlər
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageSafe = Math.min(page, totalPages);
  const paged = useMemo(
    () => sorted.slice((pageSafe - 1) * pageSize, pageSafe * pageSize),
    [sorted, pageSafe, pageSize]
  );
  useEffect(() => { setPage(1); }, [datePreset, customFrom, customTo, statusFilter, cityFilter, query, pageSize, sortKey]);

  // ── Filterə uyğun cəm hesabatları
  const summary = useMemo(() => {
    const count = filtered.length;
    const total = filtered.reduce((s, o) => s + Number(o.total_azn || 0), 0);
    const avg = count ? total / count : 0;
    return { count, total, avg };
  }, [filtered]);

  // ── Üstdə hesabat kartları (Bu gün, Dünən, Bu ay) — tam həmişə canlı
  const stats = useMemo(() => {
    const now = new Date();
    const [tFrom, tTo] = [startOfDay(now), endOfDay(now)];
    const yest = new Date(now); yest.setDate(yest.getDate() - 1);
    const [yFrom, yTo] = [startOfDay(yest), endOfDay(yest)];
    const monthFrom = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1));

    let todayN = 0, todayT = 0, yN = 0, yT = 0, mN = 0, mT = 0;
    for (const o of items) {
      const dt = toDate(o.created_at);
      if (!dt) continue;
      if (o.status === "cancelled") continue;
      const total = Number(o.total_azn || 0);
      if (dt >= tFrom && dt <= tTo)   { todayN += 1; todayT += total; }
      if (dt >= yFrom && dt <= yTo)   { yN += 1;    yT += total; }
      if (dt >= monthFrom && dt <= tTo){ mN += 1;   mT += total; }
    }
    return { today: { n: todayN, t: todayT }, yesterday: { n: yN, t: yT }, month: { n: mN, t: mT } };
  }, [items]);

  // ── Toplu seçim köməkçiləri
  const allOnPageSelected = paged.length > 0 && paged.every((o) => selectedIds.has(o.id));
  const toggleOne = (id) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAllOnPage = () => {
    setSelectedIds((s) => {
      const n = new Set(s);
      if (allOnPageSelected) { for (const o of paged) n.delete(o.id); }
      else { for (const o of paged) n.add(o.id); }
      return n;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // ── Filterləri sıfırla
  const clearAll = () => {
    setDatePreset("all"); setCustomFrom(""); setCustomTo("");
    setStatusFilter("all"); setCityFilter("all"); setQuery("");
  };
  const hasActiveFilter = datePreset !== "all" || statusFilter !== "all"
    || cityFilter !== "all" || query.trim() || customFrom || customTo;

  // ── CSV ixrac
  const exportCsv = () => {
    const rows = filtered.map((o) => ({
      "Tracking №": o.tracking_no || "",
      "Tarix": formatDateTime(toDate(o.created_at)),
      "Müştəri": o.full_name || "",
      "Telefon": o.phone || "",
      "Şəhər": o.city || "",
      "Ünvan": o.address || "",
      "Çatdırılma": o.delivery_code || "",
      "Məhsul sayı": sumQty(o.items),
      "Cəm (AZN)": Number(o.total_azn || 0).toFixed(2),
      "Endirim (AZN)": Number(o.discount_azn || 0).toFixed(2),
      "Status": STATUS_LABEL[o.status] || o.status || "",
      "Ödəniş": o.payment?.transaction || "",
      "Qeyd": o.notes || "",
      "Admin qeyd": o.admin_note || "",
    }));
    const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
    downloadCsv(`sifarisler-${stamp}.csv`, rows);
  };

  // ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-10" data-testid="admin-orders">
      {/* Başlıq & xülasə */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="metadata">YERİNƏ YETİRMƏ</div>
          <h1 className="font-display text-5xl mt-1">Sifarişlər</h1>
          <div className="mt-2 text-xs text-[var(--text-2)] flex items-center gap-2">
            <span>Yeni sifarişlər üçün izləmə formatı:</span>
            <code className="font-mono text-[var(--primary)]">{trackingPrefix}-XXXXXX</code>
            <button
              onClick={() => { setTrackingDraft(trackingPrefix); setTrackingEditOpen(true); }}
              className="text-[var(--text-2)] hover:text-[var(--primary)] inline-flex items-center gap-1"
              data-testid="tracking-prefix-edit"
              type="button"
            >
              <Settings size={12} /> Dəyiş
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={reload} className="btn-outline" title="Yenilə" data-testid="orders-refresh">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> YENİLƏ
          </button>
          <button
            onClick={exportCsv}
            className="btn-outline"
            disabled={!filtered.length}
            title="Filtrlənmiş sifarişləri CSV faylı kimi yüklə"
            data-testid="orders-export-csv"
          >
            <Download size={14} /> CSV İXRAC
          </button>
        </div>
      </div>

      {/* İzləmə nömrəsi formatı modalı */}
      {trackingEditOpen && (
        <TrackingPrefixModal
          value={trackingDraft}
          onChange={setTrackingDraft}
          onClose={() => setTrackingEditOpen(false)}
          onSave={saveTrackingPrefixHandler}
          saving={trackingSaving}
          saved={trackingSaved}
        />
      )}

      {/* Statistika kartları */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-6">
        <StatCard label="Bu günün sifarişləri" count={stats.today.n} amount={stats.today.t} />
        <StatCard label="Dünən" count={stats.yesterday.n} amount={stats.yesterday.t} />
        <StatCard label="Bu ay" count={stats.month.n} amount={stats.month.t} />
        <StatCard label="Filtrə uyğun" count={summary.count} amount={summary.total} sub={`Orta: ${formatMoney(summary.avg)}`} />
      </div>

      {/* ── Filtr paneli ─────────────────────────────────────────── */}
      <div className="mt-6 border border-[var(--border)] bg-[var(--surface)]/30 p-4 space-y-3">
        {/* Tarix dövrü */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="metadata text-[var(--text-muted)] flex items-center gap-1 mr-2">
            <Calendar size={12} /> TARİX:
          </span>
          {DATE_PRESETS.map(([k, label]) => (
            <button
              key={k}
              onClick={() => setDatePreset(k)}
              className={`px-3 py-1.5 metadata text-xs border transition ${
                datePreset === k
                  ? "bg-[var(--primary)] text-[var(--inverse)] border-[var(--primary)]"
                  : "border-[var(--border)] hover:border-[var(--primary)]"
              }`}
              data-testid={`order-date-${k}`}
            >
              {label.toUpperCase()}
            </button>
          ))}
        </div>

        {datePreset === "custom" && (
          <div className="flex items-center gap-3 pl-2 flex-wrap">
            <span className="metadata text-[var(--text-muted)] text-xs">BAŞLANĞIC</span>
            <input type="date" className="bg-transparent border border-[var(--border)] px-2 py-1 text-sm" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
            <span className="metadata text-[var(--text-muted)] text-xs">SON</span>
            <input type="date" className="bg-transparent border border-[var(--border)] px-2 py-1 text-sm" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
          </div>
        )}

        {/* Status filtrləri */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="metadata text-[var(--text-muted)] flex items-center gap-1 mr-2">
            <Filter size={12} /> STATUS:
          </span>
          <button
            onClick={() => setStatusFilter("all")}
            className={`px-3 py-1.5 metadata text-xs border ${statusFilter === "all" ? "bg-[var(--primary)] text-[var(--inverse)] border-[var(--primary)]" : "border-[var(--border)] hover:border-[var(--primary)]"}`}
            data-testid="order-status-all"
          >
            HAMISI <span className="opacity-60">({statusCounts.all || 0})</span>
          </button>
          {STATUSES.map(([s, label]) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 metadata text-xs border ${statusFilter === s ? "bg-[var(--primary)] text-[var(--inverse)] border-[var(--primary)]" : "border-[var(--border)] hover:border-[var(--primary)]"}`}
              data-testid={`order-status-${s}`}
            >
              {label.toUpperCase()} <span className="opacity-60">({statusCounts[s] || 0})</span>
            </button>
          ))}
        </div>

        {/* Şəhər + Sıralama + Axtarış */}
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            className="bg-transparent border border-[var(--border)] px-3 py-1.5 text-sm min-w-[180px]"
            data-testid="order-city-filter"
          >
            <option value="all">Bütün şəhərlər</option>
            {cityOptions.map(([city, n]) => (
              <option key={city} value={city}>{city} ({n})</option>
            ))}
          </select>

          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            className="bg-transparent border border-[var(--border)] px-3 py-1.5 text-sm"
            data-testid="order-sort"
          >
            {SORT_OPTIONS.map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>

          <div className="flex items-center gap-2 border border-[var(--border)] px-3 py-1.5 flex-1 min-w-[260px] bg-[var(--bg)]">
            <Search size={14} className="text-[var(--text-muted)]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="İzləmə №, müştəri adı, telefon, e-poçt, şəhər, ünvan…"
              className="flex-1 bg-transparent outline-none text-sm"
              data-testid="order-search"
            />
            {query && <button onClick={() => setQuery("")} className="text-[var(--text-muted)]" aria-label="Təmizlə"><X size={14} /></button>}
          </div>

          {hasActiveFilter && (
            <button onClick={clearAll} className="metadata text-xs px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)]" data-testid="order-clear-filters">
              FİLTRLƏRİ TƏMİZLƏ
            </button>
          )}
        </div>
      </div>

      {/* ── Toplu əməliyyat paneli ─────────────────────────────── */}
      {selectedIds.size > 0 && (
        <div className="mt-4 border border-[var(--primary)] bg-[var(--surface)] p-3 flex items-center gap-3 flex-wrap">
          <span className="metadata text-[var(--primary)]">{selectedIds.size} SİFARİŞ SEÇİLİB</span>
          <span className="text-[var(--text-muted)] text-xs">Status təyin et:</span>
          <div className="flex flex-wrap gap-1">
            {STATUSES.map(([s, label]) => (
              <button
                key={s}
                onClick={() => bulkSetStatus(s)}
                disabled={bulkBusy}
                className="metadata text-[10px] px-2 py-1 border border-[var(--border)] hover:border-[var(--primary)] hover:text-[var(--primary)] disabled:opacity-50"
                data-testid={`bulk-status-${s}`}
              >
                {label.toUpperCase()}
              </button>
            ))}
          </div>
          <button onClick={clearSelection} className="metadata text-xs link-underline ml-auto" data-testid="bulk-clear">
            SEÇİMİ LƏĞV ET
          </button>
        </div>
      )}

      {/* ── Cədvəl ───────────────────────────────────────────────── */}
      <div className="mt-6 border border-[var(--border)] overflow-x-auto">
        <div className="grid grid-cols-[40px_1.6fr_1.8fr_1fr_0.8fr_1.1fr_1.1fr] metadata px-4 py-3 border-b border-[var(--border)] bg-[var(--surface)] min-w-[1000px] items-center gap-2">
          <button onClick={toggleAllOnPage} className="flex items-center" aria-label="Hamısını seç" title="Bu səhifədəki hamısını seç" data-testid="select-page">
            {allOnPageSelected ? <CheckSquare size={16} /> : <Square size={16} />}
          </button>
          <div>İZLƏMƏ №</div>
          <div>MÜŞTƏRİ / ƏLAQƏ</div>
          <div>ŞƏHƏR</div>
          <div className="text-right">CƏM</div>
          <div>STATUS</div>
          <div className="text-right">TARİX</div>
        </div>

        {paged.map((o) => {
          const dt = toDate(o.created_at);
          const itemsCnt = sumQty(o.items);
          const sel = selectedIds.has(o.id);
          return (
            <div
              key={o.id}
              className={`grid grid-cols-[40px_1.6fr_1.8fr_1fr_0.8fr_1.1fr_1.1fr] px-4 py-3 border-b border-[var(--border-soft)] items-center text-sm w-full text-left hover:bg-[var(--surface)] min-w-[1000px] gap-2 ${sel ? "bg-[var(--surface)]/60" : ""}`}
              data-testid={`order-row-${o.id}`}
            >
              <button onClick={() => toggleOne(o.id)} className="flex items-center" aria-label="Seç" data-testid={`select-${o.id}`}>
                {sel ? <CheckSquare size={16} className="text-[var(--primary)]" /> : <Square size={16} className="text-[var(--text-muted)]" />}
              </button>
              <button onClick={() => setActive(o)} className="text-left font-mono text-xs" data-testid={`open-${o.id}`}>
                <div className="text-[var(--text)]">{o.tracking_no || "—"}</div>
                <div className="metadata text-[var(--text-muted)] mt-1 flex items-center gap-1">
                  <PackageIcon size={10} /> {itemsCnt} məhsul
                </div>
              </button>
              <button onClick={() => setActive(o)} className="text-left">
                <div className="font-display">{o.full_name || "—"}</div>
                <div className="metadata text-[var(--text-muted)] mt-1">{o.phone || "—"}</div>
              </button>
              <button onClick={() => setActive(o)} className="text-left text-[var(--text-2)] truncate">{o.city || "—"}</button>
              <button onClick={() => setActive(o)} className="font-mono text-right">{formatMoney(o.total_azn)}</button>
              <button onClick={() => setActive(o)} className={`metadata text-left ${STATUS_COLOR[o.status] || ""}`}>
                {(STATUS_LABEL[o.status] || o.status || "—").toUpperCase()}
              </button>
              <button onClick={() => setActive(o)} className="text-right metadata text-[var(--text-muted)]">
                {dt ? formatDateTime(dt) : "—"}
              </button>
            </div>
          );
        })}

        {!paged.length && (
          <div className="p-10 text-center text-[var(--text-muted)] metadata">
            {loading ? "Yüklənir…" : (items.length === 0 ? "Hələ sifariş yoxdur" : "Seçilən filtrə uyğun sifariş yoxdur")}
          </div>
        )}
      </div>

      {/* ── Səhifələmə ────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
        <div className="metadata text-[var(--text-muted)] text-xs">
          {sorted.length
            ? `${(pageSafe - 1) * pageSize + 1}–${Math.min(pageSafe * pageSize, sorted.length)} / ${sorted.length} sifariş`
            : "Nəticə yoxdur"}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="metadata text-[var(--text-muted)] text-xs">SƏHİFƏDƏ:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="bg-transparent border border-[var(--border)] px-2 py-1 text-sm"
              data-testid="page-size"
            >
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pageSafe <= 1}
              className="p-2 border border-[var(--border)] hover:border-[var(--primary)] disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Əvvəlki səhifə"
              data-testid="page-prev"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="metadata px-3">{pageSafe} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={pageSafe >= totalPages}
              className="p-2 border border-[var(--border)] hover:border-[var(--primary)] disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Növbəti səhifə"
              data-testid="page-next"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Detal pəncərəsi (yenilənmiş, Title Case dizayn) ─────── */}
      {active && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-end" onClick={() => setActive(null)}>
          <div className="bg-[var(--bg)] border-l border-[var(--border)] w-full max-w-2xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[var(--bg)] border-b border-[var(--border)] px-8 py-4 flex items-center justify-between z-10">
              <div>
                <div className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Sifariş</div>
                <div className="font-display text-2xl font-mono">{active.tracking_no || "—"}</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => printOrder(active)}
                  className="btn-outline inline-flex items-center gap-2"
                  style={{ textTransform: "none", letterSpacing: "0" }}
                  title="Çap et"
                  data-testid="order-print"
                >
                  <Printer size={14} /> Çap
                </button>
                <button onClick={() => setActive(null)} className="p-2" aria-label="Bağla" data-testid="order-close"><X size={18} /></button>
              </div>
            </div>

            <div className="p-8 space-y-6">
              {/* Müştəri info — Title Case labels */}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Ad" value={<span className="font-display text-base">{active.full_name || "—"}</span>} />
                <Field
                  label="Telefon"
                  value={
                    active.phone
                      ? <a href={`tel:${active.phone}`} className="link-underline flex items-center gap-1 font-display text-base"><Phone size={12} /> {active.phone}</a>
                      : <span className="text-[var(--text-muted)]">—</span>
                  }
                />
                {active.email && (
                  <Field
                    label="E-poçt"
                    full
                    value={<a href={`mailto:${active.email}`} className="link-underline flex items-center gap-1 font-display text-base"><Mail size={12} /> {active.email}</a>}
                  />
                )}
                <Field
                  label="Şəhər"
                  value={<span className="font-display text-base flex items-center gap-1"><MapPin size={12} /> {active.city || "—"}</span>}
                />
                <Field
                  label="Çatdırılma"
                  value={
                    <span className="font-display text-base">
                      {active.delivery_code || "—"}
                      {active.delivery_price_azn ? ` (${formatMoney(active.delivery_price_azn)})` : ""}
                    </span>
                  }
                />
                <Field label="Ünvan" full value={<span className="font-display text-base">{active.address || "—"}</span>} />
                {active.notes && (
                  <Field
                    label="Müştəri qeydi"
                    full
                    value={<span className="italic text-sm border-l-2 border-[var(--border)] pl-3 block">{active.notes}</span>}
                  />
                )}
                {active.payment && (
                  <Field
                    label="Ödəniş"
                    full
                    value={
                      <span className="font-mono text-xs flex items-center gap-2 flex-wrap">
                        <CreditCard size={12} />
                        <span>{active.payment.provider || "epoint"}</span>
                        {active.payment.transaction && <span className="text-[var(--text-muted)]">· №{active.payment.transaction}</span>}
                        {toDate(active.paid_at) && <span className="text-[var(--text-muted)]">· {formatDateTime(toDate(active.paid_at))}</span>}
                      </span>
                    }
                  />
                )}
                {active.coupon_code && (
                  <Field label="Kupon" value={<span className="font-mono text-sm">{active.coupon_code}</span>} />
                )}
                {Number(active.discount_azn || 0) > 0 && (
                  <Field label="Endirim" value={<span className="font-mono text-sm">−{formatMoney(active.discount_azn)}</span>} />
                )}
                <Field label="Yaranma" value={<span className="font-mono text-xs">{formatDateTime(toDate(active.created_at))}</span>} />
                <Field label="Yenilənmə" value={<span className="font-mono text-xs">{formatDateTime(toDate(active.updated_at)) || "—"}</span>} />
              </div>

              {/* Məhsullar */}
              <div>
                <div className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium mb-2">
                  Məhsullar ({sumQty(active.items)})
                </div>
                <div className="space-y-2">
                  {(active.items || []).map((i, k) => (
                    <div key={k} className="flex items-center gap-3 border border-[var(--border-soft)] p-2">
                      {i.cover_url && <img src={i.cover_url} alt="" className="w-12 h-12 object-cover" />}
                      <div className="flex-1">
                        <div className="font-display">{i.title}</div>
                        <div className="text-[11px] text-[var(--text-muted)]">{i.artist_name}</div>
                      </div>
                      <div className="font-mono text-sm">×{i.qty}</div>
                      <div className="font-mono text-sm w-24 text-right">{formatMoney(Number(i.price_azn) * Number(i.qty || 1))}</div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center mt-3">
                  <span className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Cəm</span>
                  <span className="font-display text-xl">{formatMoney(active.total_azn)}</span>
                </div>
              </div>

              {/* Status dəyiş */}
              <div>
                <div className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium mb-2">Status dəyiş</div>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map(([s, label]) => (
                    <button
                      key={s}
                      onClick={() => setStatus(active, s)}
                      className={`px-3 py-1.5 text-xs border transition ${active.status === s ? "bg-[var(--primary)] text-[var(--inverse)] border-[var(--primary)]" : "border-[var(--border)] hover:border-[var(--primary)]"}`}
                      style={{ textTransform: "none", letterSpacing: "0" }}
                      data-testid={`detail-status-${s}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Status tarixçəsi — gizli statusları da düzgün adla göstərir */}
              {(active.status_history || []).length > 0 && (
                <div>
                  <div className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium mb-2">Status tarixçəsi</div>
                  <div className="space-y-1 text-sm">
                    {(active.status_history || []).map((h, i) => (
                      <div key={i} className="flex items-center justify-between border-l-2 border-[var(--border-soft)] pl-3 py-1">
                        <span className="text-[13px] text-[var(--text)]">{FULL_STATUS_LABEL[h.status] || h.status || "—"}</span>
                        <span className="text-[11px] text-[var(--text-muted)] font-mono">{formatDateTime(toDate(h.ts))}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Müştəriyə açıq qeyd */}
              <div className="border border-[var(--primary)] bg-[var(--surface)]/40 p-4">
                <div className="text-[11px] tracking-[0.04em] text-[var(--primary)] font-medium mb-2 flex items-center gap-2">
                  <MessageSquare size={12} /> Müştəriyə açıq qeyd (sifarişlərim səhifəsində görünür)
                </div>
                <textarea
                  rows={3}
                  value={customerNote}
                  onChange={(e) => setCustomerNote(e.target.value)}
                  placeholder="İstəyə bağlıdır. Məs. çatdırılma haqqında xüsusi məlumat…"
                  className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-2 text-sm focus:border-[var(--primary)] outline-none"
                  data-testid="customer-visible-note"
                />
                <div className="flex justify-end mt-2 items-center gap-3">
                  {customerNoteSaved && <span className="text-xs text-[var(--primary)]">Yadda saxlandı</span>}
                  <button
                    onClick={saveCustomerNote}
                    disabled={customerNoteSaving || customerNote === (active.customer_visible_note || "")}
                    className="btn-primary disabled:opacity-50"
                    style={{ textTransform: "none", letterSpacing: "0" }}
                    data-testid="customer-note-save"
                  >
                    {customerNoteSaving ? "Yazılır…" : "Qeydi yadda saxla"}
                  </button>
                </div>
              </div>

              {/* Daxili qeyd (yalnız admin) */}
              <div>
                <div className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium mb-2 flex items-center gap-2">
                  <MessageSquare size={12} /> Daxili qeyd (müştəriyə görünmür)
                </div>
                <textarea
                  ref={noteRef}
                  rows={3}
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder="İstəyə bağlıdır. Sifariş haqqında daxili qeyd…"
                  className="w-full bg-transparent border border-[var(--border)] px-3 py-2 text-sm focus:border-[var(--primary)] outline-none"
                  data-testid="admin-note"
                />
                <div className="flex justify-end mt-2">
                  <button
                    onClick={saveAdminNote}
                    disabled={noteSaving || adminNote === (active.admin_note || "")}
                    className="btn-outline disabled:opacity-50"
                    style={{ textTransform: "none", letterSpacing: "0" }}
                    data-testid="admin-note-save"
                  >
                    {noteSaving ? "Yazılır…" : "Daxili qeydi saxla"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Statistika kartı
// ─────────────────────────────────────────────────────────────────────
function StatCard({ label, count, amount, sub }) {
  return (
    <div className="border border-[var(--border)] p-4 bg-[var(--surface)]/30">
      <div className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">{label}</div>
      <div className="flex items-baseline gap-2 mt-2">
        <span className="font-display text-3xl">{count}</span>
        <span className="text-[11px] tracking-[0.04em] text-[var(--text-muted)]">sifariş</span>
      </div>
      <div className="font-mono text-base mt-1">{formatMoney(amount)}</div>
      {sub && <div className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] mt-1">{sub}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Detal pəncərəsi sahəsi — Title Case etiket + dəyər
// ─────────────────────────────────────────────────────────────────────
function Field({ label, value, full = false }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">{label}</div>
      <div className="mt-1 text-[var(--text)]">{value}</div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// İzləmə nömrəsi prefiksini redaktə edən modal
// (məs. "VAZ-2026" → "VAZ-2026-03Y950" şəklində nömrələr yaradılır)
// ─────────────────────────────────────────────────────────────────────
function TrackingPrefixModal({ value, onChange, onClose, onSave, saving, saved }) {
  // Nümunə nömrə — istifadəçi yazarkən canlı yenilənir
  const preview = useMemo(() => {
    const clean = String(value || "")
      .toUpperCase()
      .replace(/[^A-Z0-9\-]/g, "")
      .replace(/-+$/, "");
    return generateTrackingNo(clean || DEFAULT_TRACKING_PREFIX);
  }, [value]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-[var(--bg)] border border-[var(--border)] max-w-md w-full p-7"
        onClick={(e) => e.stopPropagation()}
        data-testid="tracking-prefix-modal"
      >
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <div className="metadata text-[var(--text-muted)]">SİFARİŞ TƏNZİMLƏMƏSİ</div>
            <h2 className="font-display text-2xl mt-1">İzləmə nömrəsi formatı</h2>
          </div>
          <button onClick={onClose} className="text-[var(--text-2)] hover:text-[var(--text)]" aria-label="Bağla">
            <X size={18} />
          </button>
        </div>

        <p className="text-sm text-[var(--text-2)] mb-4 leading-relaxed">
          Yeni sifarişlər üçün izləmə nömrəsinin başlanğıc hissəsini təyin edin.
          Mövcud sifarişlərə təsir etmir. Yalnız hərflər, rəqəmlər və tire (-) istifadə oluna bilər.
        </p>

        <label className="block">
          <div className="metadata text-[var(--text-muted)] mb-2">PREFİKS</div>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            maxLength={24}
            placeholder={DEFAULT_TRACKING_PREFIX}
            className="w-full border border-[var(--border)] bg-[var(--bg)] px-3 py-2.5 font-mono text-base uppercase tracking-wider focus:outline-none focus:border-[var(--primary)]"
            data-testid="tracking-prefix-input"
            autoFocus
          />
        </label>

        <div className="mt-4 bg-[var(--surface)]/50 border border-[var(--border-soft)] p-3">
          <div className="metadata text-[var(--text-muted)] mb-1">NÜMUNƏ</div>
          <div className="font-mono text-lg text-[var(--primary)]" data-testid="tracking-prefix-preview">{preview}</div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn-outline" type="button" data-testid="tracking-prefix-cancel">
            LƏĞV ET
          </button>
          <button
            onClick={onSave}
            disabled={saving || !value.trim()}
            className="btn-primary disabled:opacity-50 inline-flex items-center gap-2"
            type="button"
            data-testid="tracking-prefix-save"
          >
            {saved
              ? <><CheckIcon size={14} /> Yadda saxlandı</>
              : (saving ? "Yazılır…" : "Yadda saxla")}
          </button>
        </div>
      </div>
    </div>
  );
}
